/**
 * Payment Page - Subscription activation via Razorpay
 * This page is shown when user's trial has expired
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Container,
  Box,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Grid,
  IconButton,
  AppBar,
  Toolbar,
} from '@mui/material';
import {
  CreditCard as CreditCardIcon,
  CheckCircle as CheckCircleIcon,
  ArrowBack as ArrowBackIcon,
  Lock as LockIcon,
} from '@mui/icons-material';
import { toast } from 'react-hot-toast';
import PageTitle from '../components/PageTitle';
import { createPaymentOrder, verifyPayment } from '../services/paymentService';

/**
 * Payment Page Component
 */
const PaymentPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [creatingOrder, setCreatingOrder] = useState(false);
  const [error, setError] = useState('');
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [paymentOrder, setPaymentOrder] = useState(null);

  // Get subscription data from route state or fetch it
  useEffect(() => {
    const data = location.state?.subscriptionData;
    if (data) {
      setSubscriptionData(data);
    } else {
      // If no data, redirect back to login
      toast.error('Please login first');
      navigate('/');
    }
  }, [location, navigate]);

  // Load Razorpay script
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  const handleCreateOrder = async () => {
    if (!subscriptionData || !subscriptionData.companyId) {
      setError('Company information not found. Please try logging in again.');
      return;
    }

    try {
      setCreatingOrder(true);
      setError('');

      const orderData = {
        companyId: subscriptionData.companyId,
        billingCycle: billingCycle,
      };

      const response = await createPaymentOrder(orderData);
      setPaymentOrder(response);

      // Open Razorpay checkout
      openRazorpayCheckout(response);
    } catch (err) {
      console.error('Error creating payment order:', err);
      setError(err.message || 'Failed to create payment order. Please try again.');
      toast.error(err.message || 'Failed to create payment order');
    } finally {
      setCreatingOrder(false);
    }
  };

  const openRazorpayCheckout = (orderData) => {
    if (!window.Razorpay) {
      setError('Razorpay checkout is not loaded. Please refresh the page.');
      return;
    }

    const options = {
      key: orderData.keyId,
      amount: Math.round(orderData.amount * 100), // Convert to paise
      currency: orderData.currency || 'INR',
      name: 'Tiya Technology',
      description: `Subscription Activation - ${orderData.planName}`,
      order_id: orderData.orderId,
      handler: async function (response) {
        // Payment successful, verify payment
        try {
          setLoading(true);
          const verificationData = {
            orderId: response.razorpay_order_id,
            paymentId: response.razorpay_payment_id,
            signature: response.razorpay_signature,
          };

          const verifyResponse = await verifyPayment(verificationData);

          if (verifyResponse.status === 'success' && verifyResponse.subscriptionActivated) {
            toast.success('Payment successful! Your subscription has been activated.');
            // Redirect to login after 2 seconds
            setTimeout(() => {
              navigate('/', { 
                state: { 
                  paymentSuccess: true,
                  message: 'Your subscription has been activated. Please login to continue.' 
                } 
              });
            }, 2000);
          } else {
            throw new Error(verifyResponse.message || 'Payment verification failed');
          }
        } catch (err) {
          console.error('Payment verification error:', err);
          setError(err.message || 'Payment verification failed. Please contact support.');
          toast.error(err.message || 'Payment verification failed');
        } finally {
          setLoading(false);
        }
      },
      prefill: {
        email: subscriptionData?.email || '',
      },
      theme: {
        color: '#667eea',
      },
      modal: {
        ondismiss: function () {
          toast.info('Payment cancelled');
        },
      },
    };

    const razorpay = new window.Razorpay(options);
    razorpay.open();
  };

  if (!subscriptionData) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  const planName = subscriptionData.planName || 'Your Selected Plan';
  const monthlyAmount = subscriptionData.monthlyAmount || 0;
  const yearlyAmount = subscriptionData.yearlyAmount || 0;
  const selectedAmount = billingCycle === 'yearly' ? yearlyAmount : monthlyAmount;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5' }}>
      {/* Navbar */}
      <AppBar 
        position="static" 
        sx={{ 
          backgroundColor: '#1976d2',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        }}
      >
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={() => navigate('/')}
            sx={{ mr: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography 
            variant="h6" 
            component="div" 
            sx={{ 
              flexGrow: 1,
              fontWeight: 600,
              fontSize: '1.25rem',
            }}
          >
            Activate Your Subscription
          </Typography>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ py: 4 }}>
        <PageTitle title="Activate Subscription" />
        
        {error && (
          <Alert 
            severity="error" 
            sx={{ 
              mb: 3,
              borderRadius: 1,
            }} 
            onClose={() => setError('')}
          >
            {error}
          </Alert>
        )}

        <Grid container spacing={3} sx={{ mt: 1 }}>
          {/* Plan Details Card */}
          <Grid item xs={12} md={6}>
            <Card 
              sx={{ 
                height: '100%',
                borderRadius: 2,
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                border: '1px solid #e0e0e0',
              }}
            >
              <CardContent sx={{ p: 3 }}>
                {/* Plan Header */}
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <CheckCircleIcon 
                    sx={{ 
                      mr: 1.5, 
                      fontSize: 32,
                      color: '#1976d2',
                    }} 
                  />
                  <Typography 
                    variant="h5" 
                    sx={{ 
                      fontWeight: 600,
                      color: '#1976d2',
                      fontSize: '1.5rem',
                    }}
                  >
                    {planName}
                  </Typography>
                </Box>

                <Divider sx={{ my: 2.5, borderColor: '#e0e0e0' }} />

                {/* Billing Cycle Selection */}
                <Box sx={{ mb: 3 }}>
                  <FormControl fullWidth>
                    <InputLabel sx={{ color: '#757575' }}>Billing Cycle</InputLabel>
                    <Select
                      value={billingCycle}
                      label="Billing Cycle"
                      onChange={(e) => setBillingCycle(e.target.value)}
                      sx={{
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#e0e0e0',
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#1976d2',
                        },
                      }}
                    >
                      <MenuItem value="monthly">
                        Monthly - ₹{monthlyAmount.toLocaleString('en-IN')}
                      </MenuItem>
                      <MenuItem value="yearly">
                        Yearly - ₹{yearlyAmount.toLocaleString('en-IN')} 
                        {yearlyAmount > 0 && monthlyAmount > 0 && (
                          <Typography 
                            component="span" 
                            variant="caption" 
                            sx={{ 
                              ml: 1, 
                              color: '#2e7d32',
                              fontWeight: 600,
                            }}
                          >
                            (Save ₹{(monthlyAmount * 12 - yearlyAmount).toLocaleString('en-IN')})
                          </Typography>
                        )}
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                {/* Amount Display */}
                <Box sx={{ 
                  p: 3, 
                  backgroundColor: '#1976d2', 
                  borderRadius: 1,
                  textAlign: 'center',
                  boxShadow: '0 2px 8px rgba(25, 118, 210, 0.2)',
                }}>
                  <Typography 
                    variant="h4" 
                    sx={{ 
                      fontWeight: 700, 
                      color: 'white',
                      mb: 0.5,
                    }}
                  >
                    ₹{selectedAmount.toLocaleString('en-IN')}
                  </Typography>
                  <Typography 
                    variant="body2" 
                    sx={{
                      color: 'rgba(255,255,255,0.9)',
                      fontWeight: 500,
                    }}
                  >
                    {billingCycle === 'yearly' ? 'per year' : 'per month'}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Payment Summary Card */}
          <Grid item xs={12} md={6}>
            <Card 
              sx={{ 
                height: '100%',
                borderRadius: 2,
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                border: '1px solid #e0e0e0',
              }}
            >
              <CardContent sx={{ p: 3, display: 'flex', flexDirection: 'column', height: '100%' }}>
                {/* Payment Header */}
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <LockIcon 
                    sx={{ 
                      mr: 1.5, 
                      fontSize: 32,
                      color: '#1976d2',
                    }} 
                  />
                  <Typography 
                    variant="h5" 
                    sx={{ 
                      fontWeight: 600,
                      color: '#1976d2',
                      fontSize: '1.5rem',
                    }}
                  >
                    Secure Payment
                  </Typography>
                </Box>

                <Divider sx={{ my: 2.5, borderColor: '#e0e0e0' }} />

                {/* Payment Summary */}
                <Box sx={{ flexGrow: 1 }}>
                  <Box sx={{ mb: 2.5 }}>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: '#757575',
                        fontWeight: 500,
                        mb: 0.5,
                      }}
                    >
                      Plan
                    </Typography>
                    <Typography 
                      variant="body1" 
                      sx={{ 
                        fontWeight: 600,
                        color: '#424242',
                      }}
                    >
                      {planName}
                    </Typography>
                  </Box>

                  <Box sx={{ mb: 2.5 }}>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: '#757575',
                        fontWeight: 500,
                        mb: 0.5,
                      }}
                    >
                      Billing Cycle
                    </Typography>
                    <Typography 
                      variant="body1" 
                      sx={{ 
                        fontWeight: 600, 
                        textTransform: 'capitalize',
                        color: '#424242',
                      }}
                    >
                      {billingCycle}
                    </Typography>
                  </Box>

                  <Divider sx={{ my: 2.5, borderColor: '#e0e0e0' }} />

                  <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography 
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          color: '#424242',
                        }}
                      >
                        Amount
                      </Typography>
                      <Typography 
                        variant="h6" 
                        sx={{ 
                          fontWeight: 700,
                          color: '#1976d2',
                          fontSize: '1.5rem',
                        }}
                      >
                        ₹{selectedAmount.toLocaleString('en-IN')}
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                {/* Payment Button */}
                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  onClick={handleCreateOrder}
                  disabled={creatingOrder || loading || selectedAmount === 0}
                  startIcon={creatingOrder ? <CircularProgress size={18} color="inherit" /> : <CreditCardIcon />}
                  sx={{
                    py: 1.5,
                    backgroundColor: '#1976d2',
                    color: 'white',
                    fontWeight: 600,
                    textTransform: 'none',
                    boxShadow: '0 2px 8px rgba(25, 118, 210, 0.3)',
                    '&:hover': {
                      backgroundColor: '#1565c0',
                      boxShadow: '0 4px 12px rgba(25, 118, 210, 0.4)',
                    },
                    '&:disabled': {
                      backgroundColor: '#90caf9',
                    }
                  }}
                >
                  {creatingOrder ? 'Creating Order...' : 'Pay with Razorpay'}
                </Button>

                {/* Security Footer */}
                <Box sx={{ mt: 2, textAlign: 'center' }}>
                  <Typography 
                    variant="caption" 
                    sx={{
                      color: '#757575',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 0.5,
                    }}
                  >
                    <LockIcon sx={{ fontSize: 14 }} />
                    Secure payment powered by Razorpay
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {loading && (
          <Box sx={{ 
            position: 'fixed', 
            top: 0, 
            left: 0, 
            right: 0, 
            bottom: 0, 
            bgcolor: 'rgba(0,0,0,0.5)', 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center',
            zIndex: 9999
          }}>
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <CircularProgress sx={{ mb: 2 }} />
              <Typography>Verifying payment...</Typography>
            </Paper>
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default PaymentPage;

